/**
 * La cola de subida de facturas.
 *
 * SE PUEDEN SOLTAR VARIAS, Y SEGUIR SOLTANDO. Cada factura tarda unos segundos (se lee con IA),
 * y antes, mientras subía una tanda, la tarjeta se bloqueaba y lo que se soltaba encima se perdía
 * sin aviso. Ahora todo va a una cola: diez facturas en un piso, cinco en otro y tres en «Varios
 * pisos», seguidas, y se suben de una en una por orden de llegada.
 *
 * DE UNA EN UNA, no en paralelo: cada subida crea un apunte en el cierre del mes, y diez
 * peticiones a la vez contra el mismo cierre es pedir problemas por nada.
 *
 * VIVE POR ENCIMA DE LAS PÁGINAS (en `App`): se suelta una tanda y se puede ir a la conciliación
 * mientras se sube. Si se cierra la pestaña con algo pendiente, el navegador pregunta.
 *
 * EL PANEL dice de cada factura en qué piso y en qué mes ha quedado, qué se ha entendido (concepto,
 * importe, periodo facturado) y cómo se supo el piso, que es lo que decide qué inquilinos la
 * pagan. Si no se supo, lo pregunta ahí mismo; si parece de otro piso que aquel en que se soltó,
 * lo avisa y deja moverla.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { LedgerUploadResult } from '@naar/shared';
import { MONTH_NAMES_ES } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents } from '../../lib/format';
import { avisarReapertura } from './MissingPanel';
import styles from './UploadQueue.module.css';

/** Lo que admite la subida: PDF y fotos. */
export const LEDGER_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
const ADMITIDOS = /\.(pdf|jpe?g|png|webp)$/i;
const NO_ADMITIDO = 'Solo se admiten PDF y fotos (JPG, PNG o WEBP).';

export interface DestinoSubida {
  /** El piso elegido; `null` = que lo saque del nombre del fichero o de la factura. */
  propertyId: string | null;
  /** Dónde se soltó, para el panel: el nombre del piso o «Varios pisos». */
  origen: string;
  /** Los pisos entre los que elegir si la factura no dice de cuál es. */
  pisos?: { id: string; name: string }[];
}

type Estado = 'en_cola' | 'subiendo' | 'hecho' | 'sin_piso' | 'error';

interface Elemento extends DestinoSubida {
  id: string;
  file: File;
  estado: Estado;
  progreso: number;
  resultado: LedgerUploadResult | null;
  error: string | null;
}

interface ColaValue {
  encolar: (files: File[], destino: DestinoSubida) => void;
  /** Cuántas quedan por subir de un piso (`null`: las de «Varios pisos»). */
  pendientes: (propertyId: string | null) => number;
}

const ColaContext = createContext<ColaValue | null>(null);

export function useUploadQueue(): ColaValue {
  const cola = useContext(ColaContext);
  if (!cola) throw new Error('useUploadQueue fuera de <UploadQueueProvider>');
  return cola;
}

let siguienteId = 0;

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<Elemento[]>([]);
  /** Hay una subida en marcha. Ref y no estado: el efecto no debe arrancar dos a la vez. */
  const ocupado = useRef(false);
  /** Se cambia al terminar cada subida para que el efecto mire si queda otra. */
  const [vuelta, setVuelta] = useState(0);
  /** Subidas terminadas desde el último refresco de las pantallas. */
  const sinRefrescar = useRef(0);

  const actualizar = useCallback((id: string, cambios: Partial<Elemento>) => {
    setItems((actual) => actual.map((item) => (item.id === id ? { ...item, ...cambios } : item)));
  }, []);

  const encolar = useCallback((files: File[], destino: DestinoSubida) => {
    if (files.length === 0) return;
    setItems((actual) => [
      ...actual,
      ...files.map((file): Elemento => {
        const admitido = LEDGER_ACCEPT.split(',').includes(file.type) || ADMITIDOS.test(file.name);
        siguienteId += 1;
        return {
          ...destino,
          id: `subida-${siguienteId}`,
          file,
          estado: admitido ? 'en_cola' : 'error',
          progreso: 0,
          resultado: null,
          error: admitido ? null : NO_ADMITIDO,
        };
      }),
    ]);
  }, []);

  const pendientes = useCallback(
    (propertyId: string | null) =>
      items.filter(
        (item) => item.propertyId === propertyId && (item.estado === 'en_cola' || item.estado === 'subiendo'),
      ).length,
    [items],
  );

  /* El motor: si no hay nada subiendo y hay algo en cola, sube el primero. */
  useEffect(() => {
    if (ocupado.current) return;
    const siguiente = items.find((item) => item.estado === 'en_cola');
    if (!siguiente) {
      // La cola se ha vaciado: ahora sí, que las pantallas pidan los datos nuevos (una vez).
      if (sinRefrescar.current > 0) {
        sinRefrescar.current = 0;
        void queryClient.invalidateQueries({ queryKey: ['ledger'] });
        void queryClient.invalidateQueries({ queryKey: ['utilities'] });
      }
      return;
    }
    ocupado.current = true;
    actualizar(siguiente.id, { estado: 'subiendo', progreso: 0, error: null });
    void (async () => {
      try {
        const resultado = await api.uploadTicket(siguiente.propertyId, siguiente.file, (progreso) =>
          actualizar(siguiente.id, { progreso }),
        );
        actualizar(siguiente.id, { estado: 'hecho', resultado });
        sinRefrescar.current += 1;
        if (resultado.reopened) avisarReapertura(resultado.month);
      } catch (error) {
        if (error instanceof ApiError && error.code === 'PROPERTY_UNKNOWN') {
          actualizar(siguiente.id, { estado: 'sin_piso', error: error.message });
        } else {
          actualizar(siguiente.id, {
            estado: 'error',
            error: error instanceof ApiError ? error.message : 'No se ha podido subir.',
          });
        }
      } finally {
        ocupado.current = false;
        setVuelta((n) => n + 1);
      }
    })();
  }, [items, vuelta, actualizar, queryClient]);

  /* Cerrar la pestaña con facturas sin subir las perdería: el navegador pregunta antes. */
  const quedan = items.some((item) => item.estado === 'en_cola' || item.estado === 'subiendo');
  useEffect(() => {
    if (!quedan) return;
    const avisar = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [quedan]);

  const value = useMemo<ColaValue>(() => ({ encolar, pendientes }), [encolar, pendientes]);

  return (
    <ColaContext.Provider value={value}>
      {children}
      {items.length > 0 && <Panel items={items} setItems={setItems} actualizar={actualizar} />}
    </ColaContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// El panel
// ---------------------------------------------------------------------------

function nombreDelMes(key: string): string {
  return `${MONTH_NAMES_ES[Number(key.slice(5, 7)) - 1]?.toLowerCase() ?? key} ${key.slice(0, 4)}`;
}

/** «14/07 – 12/08», con el año solo si cambia. */
function periodoCorto(periodo: { from: string; to: string }): string {
  const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  const anio = periodo.from.slice(0, 4) === periodo.to.slice(0, 4) ? '' : `/${periodo.to.slice(2, 4)}`;
  return `${dm(periodo.from)} – ${dm(periodo.to)}${anio}`;
}

const COMO_SE_SUPO: Record<LedgerUploadResult['detectedBy'], string | null> = {
  elegido: null,
  nombre: 'por el nombre del fichero',
  suministro: 'por su CUPS o contrato',
  direccion: 'por la dirección de suministro',
};

function Panel({
  items,
  setItems,
  actualizar,
}: {
  items: Elemento[];
  setItems: (cambio: (actual: Elemento[]) => Elemento[]) => void;
  actualizar: (id: string, cambios: Partial<Elemento>) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [plegado, setPlegado] = useState(false);
  const [moviendo, setMoviendo] = useState<string | null>(null);

  const total = items.length;
  const terminadas = items.filter((item) => item.estado === 'hecho').length;
  const porResolver = items.filter((item) => item.estado === 'sin_piso' || item.estado === 'error').length;
  const enMarcha = items.some((item) => item.estado === 'en_cola' || item.estado === 'subiendo');

  const titulo = enMarcha
    ? `Subiendo facturas · ${terminadas} de ${total}`
    : porResolver > 0
      ? `${porResolver === 1 ? '1 factura necesita' : `${porResolver} facturas necesitan`} algo`
      : `${terminadas === 1 ? '1 factura subida' : `${terminadas} facturas subidas`}`;

  const quitar = (id: string) => setItems((actual) => actual.filter((item) => item.id !== id));

  async function mover(item: Elemento, destino: { id: string; name: string }) {
    if (!item.resultado) return;
    setMoviendo(item.id);
    try {
      await api.updateLedgerEntry(item.resultado.id, { propertyId: destino.id });
      actualizar(item.id, { resultado: { ...item.resultado, property: destino, otherProperty: null } });
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(`Movida a ${destino.name}.`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido mover la factura.');
    } finally {
      setMoviendo(null);
    }
  }

  return (
    <section className={styles.panel} aria-label="Cola de subida de facturas">
      <header className={styles.head}>
        <span className={styles.headIcon} aria-hidden="true">
          <Icon name={enMarcha ? 'upload' : porResolver > 0 ? 'info' : 'check'} size={15} strokeWidth={2.2} />
        </span>
        <span className={styles.title} aria-live="polite">
          {titulo}
        </span>
        {!enMarcha && terminadas > 0 && porResolver > 0 && (
          <button
            type="button"
            className={styles.textButton}
            onClick={() => setItems((actual) => actual.filter((item) => item.estado !== 'hecho'))}
          >
            Quitar las subidas
          </button>
        )}
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setPlegado((valor) => !valor)}
          aria-label={plegado ? 'Desplegar la cola' : 'Plegar la cola'}
          aria-expanded={!plegado}
        >
          <Icon name={plegado ? 'chevron-left' : 'chevron-down'} size={16} strokeWidth={2.2} />
        </button>
        {!enMarcha && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setItems(() => [])}
            aria-label="Cerrar la cola"
          >
            <Icon name="close" size={15} strokeWidth={2.2} />
          </button>
        )}
      </header>

      {enMarcha && (
        <div className={styles.track} aria-hidden="true">
          <span className={styles.bar} style={{ width: `${Math.round((terminadas / total) * 100)}%` }} />
        </div>
      )}

      {!plegado && (
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.id} className={`${styles.item} ${styles[`item_${item.estado}`] ?? ''}`}>
              <span className={styles.state} aria-hidden="true">
                {item.estado === 'subiendo' ? (
                  <span className={styles.spinner} />
                ) : (
                  <Icon
                    name={item.estado === 'hecho' ? 'check' : item.estado === 'en_cola' ? 'file' : 'info'}
                    size={14}
                    strokeWidth={2.2}
                  />
                )}
              </span>
              <div className={styles.body}>
                <span className={styles.file} title={item.file.name}>
                  {item.file.name}
                </span>
                <Detalle item={item} />

                {item.estado === 'hecho' && item.resultado?.otherProperty && (
                  <p className={styles.warning}>
                    Ojo: {item.resultado.otherProperty.detalle}.{' '}
                    <button
                      type="button"
                      className={styles.linkButton}
                      disabled={moviendo === item.id}
                      onClick={() => {
                        const otro = item.resultado?.otherProperty;
                        if (otro) void mover(item, { id: otro.id, name: otro.name });
                      }}
                    >
                      {moviendo === item.id ? 'Moviendo…' : `Moverla a ${item.resultado.otherProperty.name}`}
                    </button>
                  </p>
                )}

                {item.estado === 'sin_piso' && (
                  <ElegirPiso
                    pisos={item.pisos ?? []}
                    onElegir={(piso) =>
                      actualizar(item.id, { propertyId: piso.id, origen: piso.name, estado: 'en_cola', error: null })
                    }
                  />
                )}

                {item.estado === 'hecho' && item.resultado && (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => navigate(`/ingresos-y-gastos/conciliacion?mes=${item.resultado?.month ?? ''}`)}
                  >
                    Ver en la conciliación de {nombreDelMes(item.resultado.month)}
                  </button>
                )}
                {item.estado === 'error' && item.error !== NO_ADMITIDO && (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => actualizar(item.id, { estado: 'en_cola', error: null })}
                  >
                    Reintentar
                  </button>
                )}
              </div>
              {item.estado !== 'subiendo' && (
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => quitar(item.id)}
                  aria-label={`Quitar ${item.file.name} de la lista`}
                  title={item.estado === 'en_cola' ? 'No subirla' : 'Quitar de la lista'}
                >
                  <Icon name="close" size={13} strokeWidth={2.2} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Detalle({ item }: { item: Elemento }) {
  if (item.estado === 'en_cola') return <span className={styles.meta}>En cola · {item.origen}</span>;
  if (item.estado === 'subiendo') {
    return (
      <span className={styles.meta}>
        {item.progreso < 100 ? `Subiendo ${item.progreso} %` : 'Leyendo la factura…'} · {item.origen}
      </span>
    );
  }
  if (item.estado === 'sin_piso' || item.estado === 'error') {
    return <span className={`${styles.meta} ${styles.metaProblem}`}>{item.error}</span>;
  }
  const r = item.resultado;
  if (!r) return null;
  const como = COMO_SE_SUPO[r.detectedBy];
  return (
    <span className={styles.meta}>
      <strong className={styles.what}>
        {r.concept} · {formatEurCents(r.amount)}
      </strong>{' '}
      en <strong>{r.property.name}</strong>
      {como ? ` (${como})` : ''} · {nombreDelMes(r.month)}
      {r.servicePeriod ? ` · periodo ${periodoCorto(r.servicePeriod)}` : ''}
      {r.attached ? ' · adjuntada al gasto que ya estaba apuntado' : ''}
    </span>
  );
}

function ElegirPiso({
  pisos,
  onElegir,
}: {
  pisos: { id: string; name: string }[];
  onElegir: (piso: { id: string; name: string }) => void;
}) {
  const [elegido, setElegido] = useState('');
  if (pisos.length === 0) return null;
  return (
    <div className={styles.choose}>
      <select
        className={styles.select}
        value={elegido}
        onChange={(event) => setElegido(event.target.value)}
        aria-label="Piso de la factura"
      >
        <option value="">Elige el piso…</option>
        {pisos.map((piso) => (
          <option key={piso.id} value={piso.id}>
            {piso.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={styles.chooseButton}
        disabled={!elegido}
        onClick={() => {
          const piso = pisos.find((row) => row.id === elegido);
          if (piso) onElegir(piso);
        }}
      >
        Subirla ahí
      </button>
    </div>
  );
}
